import React, { useState, useEffect } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { Collapse, TextField, Grid, Box } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Switch from 'react-switch';
import * as Utils from '../../commons/utils/Utils.js';

/**
 * VEC-3432 — Filtros del grid de Mano de Obra.
 *
 * No hay filtro de GTIN ni de marca (Scenario 03: la mano de obra no tiene esos campos), ni de
 * stock bajo mínimo (no tiene stock).
 */
const FILTER_STATE_INIT = {
  numeroDeParte: '',
  descripcion: '',
  categoria: null,
  subcategoria: null,
  showInactivos: false,
};

const InventarioManoObraGridFiltros = (props) => {
  const intl = useIntl();
  const [filtersState, setFiltersState] = useState({ ...FILTER_STATE_INIT });

  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);

  // Los selects de categorías y subcategorías se reusan de Pañol2: son GET de solo lectura y ya
  // devuelven SelectOption ({value, label}). No se duplican endpoints ni se toca el módulo legacy.
  //
  // Acá se usa la lista COMPLETA de categorías, a diferencia del ABM, que usa la filtrada por
  // "tiene subcategorías": filtrar por una categoría sin ítems devuelve cero filas, que es la
  // respuesta correcta y no deja al usuario trabado. En el ABM sí lo dejaría (no podría guardar).
  useEffect(() => {
    Utils.getData('/panol2/select/categorias')
      .then((data) => setCategorias(data || []))
      .catch(() => setCategorias([]));
  }, []);

  useEffect(() => {
    if (props.filterResetInputs) {
      setFiltersState({ ...FILTER_STATE_INIT });
      setSubcategorias([]);
      props.setFilterResetInputs && props.setFilterResetInputs(false);
    }
  }, [props.filterResetInputs]);

  useEffect(() => {
    !props.dropdownActive && props.setFilterResetInputs(false);
  }, [props.dropdownActive]);

  const triggerFilter = (state) => {
    const current = state !== undefined ? state : filtersState;
    props.handleFilterChange();
    props.setSearchActive(
      current.numeroDeParte !== '' ||
        current.descripcion !== '' ||
        !!current.categoria ||
        !!current.subcategoria
    );
    props.setFilterQuery({
      numeroDeParte: current.numeroDeParte,
      descripcion: current.descripcion,
      categoria: current.categoria ? current.categoria.value : '',
      subcategoria: current.subcategoria ? current.subcategoria.value : '',
      showInactivos: current.showInactivos,
    });
  };

  const handleChange = (name, value) => {
    setFiltersState((prev) => ({ ...prev, [name]: value }));
  };

  const handleOnEnter = (e, name) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      handleBuscar({ ...filtersState, [name]: e.target.value });
    }
  };

  const handleCategoriaChange = (value) => {
    setFiltersState((prev) => ({ ...prev, categoria: value, subcategoria: null }));
    if (value) {
      Utils.getData('/panol2/select/subcategorias?categoria=' + value.value)
        .then((data) => setSubcategorias(data || []))
        .catch(() => setSubcategorias([]));
    } else {
      setSubcategorias([]);
    }
  };

  const handleSwitch = (name, value) => {
    setFiltersState((prev) => ({ ...prev, [name]: value }));
  };

  const handleBuscar = (state) => {
    triggerFilter(state);
  };

  return (
    <Collapse in={props.dropdownActive}>
      <Grid container item xs={12} spacing={2} style={{ marginBottom: 16, marginTop: 4 }}>
        <Grid component={Box} item xs={12} sm={6} md={3} lg={2}>
          <TextField
            label={intl.formatMessage({
              id: 'inventarioManoObra.filtros.numero_de_parte',
              defaultMessage: 'Nº de parte',
            })}
            name="numeroDeParte"
            value={filtersState.numeroDeParte}
            onChange={(e) => handleChange('numeroDeParte', e.target.value)}
            onKeyDown={(e) => handleOnEnter(e, 'numeroDeParte')}
            className="col-12"
          />
        </Grid>
        <Grid component={Box} item xs={12} sm={6} md={4} lg={4}>
          <TextField
            label={intl.formatMessage({
              id: 'inventarioManoObra.filtros.descripcion',
              defaultMessage: 'Descripción',
            })}
            name="descripcion"
            value={filtersState.descripcion}
            onChange={(e) => handleChange('descripcion', e.target.value)}
            onKeyDown={(e) => handleOnEnter(e, 'descripcion')}
            className="col-12"
          />
        </Grid>
        <Grid component={Box} item xs={12} sm={6} md={3} lg={3}>
          <Autocomplete
            size="small"
            name="categoria"
            options={categorias}
            value={filtersState.categoria}
            getOptionLabel={(option) => (option ? option.label : '')}
            getOptionSelected={(option, value) => option.value === value.value}
            onChange={(_, value) => handleCategoriaChange(value)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label={intl.formatMessage({
                  id: 'inventarioManoObra.filtros.categoria',
                  defaultMessage: 'Categoría',
                })}
              />
            )}
          />
        </Grid>
        <Grid component={Box} item xs={12} sm={6} md={3} lg={3}>
          <Autocomplete
            size="small"
            name="subcategoria"
            options={subcategorias}
            value={filtersState.subcategoria}
            disabled={!filtersState.categoria}
            getOptionLabel={(option) => (option ? option.label : '')}
            getOptionSelected={(option, value) => option.value === value.value}
            onChange={(_, value) => handleChange('subcategoria', value)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                label={intl.formatMessage({
                  id: 'inventarioManoObra.filtros.subcategoria',
                  defaultMessage: 'Subcategoría',
                })}
              />
            )}
          />
        </Grid>
        <Grid container item xs={12} spacing={2} alignItems="center">
          <Grid component={Box} item xs="auto" style={{ paddingRight: 20 }}>
            <div className="d-flex align-items-center" style={{ gap: 8 }}>
              <Switch
                onChange={(value) => handleSwitch('showInactivos', value)}
                checked={filtersState.showInactivos}
                offColor="#FF4961"
                onColor="#28D094"
              />
              <FormattedMessage id="inventarioManoObra.filtros.ver_inactivos" defaultMessage="Ver Inactivos" />
            </div>
          </Grid>
          <Grid component={Box} item style={{ flex: 1 }} />
          <Grid component={Box} item xs="auto">
            <button disabled={props.loading} className="btn btn-primary" onClick={() => handleBuscar()}>
              <i className="fa fa-search fa-xs"></i> <FormattedMessage id="Buscar" defaultMessage="Buscar" />
            </button>
          </Grid>
        </Grid>
      </Grid>
    </Collapse>
  );
};

export default InventarioManoObraGridFiltros;
